import { useState, useMemo, useCallback, useEffect } from 'react';
import { usePagination } from '@/shared/hooks/usePagination';
import { Pagination } from '@/shared/components/Pagination';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/shared/contexts/LanguageContext';
import { useSignedContracts, useTemplates, useDeleteSignedContract, useSettings } from '@/shared/hooks/useCloudData';
import { useDrafts, useDeleteDraft } from '@/shared/hooks/useDrafts';
import { generateContractPdf } from '@/features/contracts/services/generatePdf';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ArrowLeft, Calendar, Mail, Eye, Download, Share2, X, Trash2, FileEdit } from 'lucide-react';
import { toast } from 'sonner';

const History = () => {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'signed' | 'drafts'>('all');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const { data: contracts = [], isLoading } = useSignedContracts();
  const { data: templates = [] } = useTemplates();
  const { data: drafts = [] } = useDrafts();
  const deleteContract = useDeleteSignedContract();
  const deleteDraft = useDeleteDraft();
  const settings = useSettings();

  const filtered = useMemo(() => {
    let list = filter === 'drafts' ? [] : contracts;
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter(
      (c) =>
        c.clientName.toLowerCase().includes(q) ||
        c.clientEmail.toLowerCase().includes(q) ||
        c.templateName.toLowerCase().includes(q)
    );
  }, [contracts, search, filter]);

  const pagination = usePagination(filtered, 20);

  // Reset to page 1 when search or filter changes
  useEffect(() => { pagination.reset(); }, [search, filter]);

  const pdfOptions = { logoDataUrl: settings.logoUrl, businessName: settings.businessName || settings.practitionerName };

  const handleExportPdf = (contractId: string) => {
    const contract = contracts.find(c => c.id === contractId);
    if (!contract) return;
    const template = templates.find(t => t.id === contract.templateId);
    generateContractPdf(contract, template, 'save', pdfOptions);
  };

  const handlePreviewPdf = (contractId: string) => {
    const contract = contracts.find(c => c.id === contractId);
    if (!contract) return;
    const template = templates.find(t => t.id === contract.templateId);
    const blob = generateContractPdf(contract, template, 'blob', pdfOptions) as Blob;
    if (blob) {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
    }
  };

  const closePreview = useCallback(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  }, [previewUrl]);

  const handleShare = async (contractId: string) => {
    const contract = contracts.find(c => c.id === contractId);
    if (!contract) return;
    const template = templates.find(t => t.id === contract.templateId);
    const blob = generateContractPdf(contract, template, 'blob', pdfOptions) as Blob;
    if (!blob) return;

    const fileName = `${contract.clientName || 'contract'}_${new Date(contract.signedAt).toISOString().slice(0, 10)}.pdf`;
    const file = new File([blob], fileName, { type: 'application/pdf' });

    try {
      // Try sharing with the PDF file attached (mobile: WhatsApp, Messenger, Mail, etc.)
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          title: contract.templateName,
          text: `${contract.templateName} — ${contract.clientName}`,
          files: [file],
        });
        return;
      }

      // Fallback: try native share without file (desktop browsers that support share API)
      if (navigator.share) {
        // Create a temporary download URL so the user can still access the PDF
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);

        await navigator.share({
          title: contract.templateName,
          text: `${contract.templateName} — ${contract.clientName}`,
        });
        return;
      }

      // Last fallback: just download the PDF
      generateContractPdf(contract, template, 'save', pdfOptions);
      toast.success(language === 'fr' ? 'PDF téléchargé' : language === 'es' ? 'PDF descargado' : 'PDF downloaded');
    } catch (err: unknown) {
      // User cancelled the share sheet — not an error
      if (err instanceof Error && err.name === 'AbortError') return;
      toast.info(language === 'fr' ? 'Partage annulé' : language === 'es' ? 'Compartir cancelado' : 'Share cancelled');
    }
  };

  const confirmDelete = () => {
    if (!deleteId) return;
    deleteContract.mutate(deleteId, {
      onSuccess: () => {
        toast.success(language === 'fr' ? 'Contrat supprimé' : language === 'es' ? 'Contrato eliminado' : 'Contract deleted');
        setDeleteId(null);
      },
    });
  };

  return (
    <>
      <div className="tablet-container pb-28 space-y-6">
        <div className="flex items-center gap-4 pt-6">
          <button onClick={() => navigate('/')} className="touch-button p-3 bg-secondary rounded-xl">
            <ArrowLeft size={24} />
          </button>
          <h1 className="section-title flex-1">{t('history.title')}</h1>
        </div>

        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('history.search')}
            className="w-full pl-12 pr-4 py-4 bg-card border-2 border-border rounded-2xl text-lg focus:border-primary focus:outline-none transition-colors"
          />
        </div>

        {/* Filter pills */}
        <div className="flex gap-2">
          {(['all', 'signed', 'drafts'] as const).map(f => {
            const labels = { all: language === 'fr' ? 'Tous' : 'All', signed: language === 'fr' ? 'Signés' : 'Signed', drafts: language === 'fr' ? 'Brouillons' : 'Drafts' };
            const counts = { all: contracts.length + drafts.length, signed: contracts.length, drafts: drafts.length };
            return (
              <button key={f} onClick={() => setFilter(f)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all
                  ${filter === f ? 'bg-primary text-primary-foreground shadow-md shadow-primary/25' : 'bg-secondary/60 text-muted-foreground hover:bg-secondary'}`}>
                {labels[f]}
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${filter === f ? 'bg-white/20' : 'bg-border/60'}`}>{counts[f]}</span>
              </button>
            );
          })}
        </div>

        {isLoading ? (
          <div className="text-center py-16">
            <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin mx-auto" />
          </div>
        ) : filter === 'drafts' ? (
          drafts.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground">
              <p className="text-6xl mb-4">📝</p>
              <p className="text-xl">{language === 'fr' ? 'Aucun brouillon' : 'No drafts'}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {drafts.filter(d => !search || d.client_name.toLowerCase().includes(search.toLowerCase()) || d.template_name.toLowerCase().includes(search.toLowerCase())).map((draft, i) => (
                <motion.div key={draft.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  className="card-elevated p-5 flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-amber-500/10 flex items-center justify-center shrink-0">
                    <FileEdit size={24} className="text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-700 border border-amber-200/50 font-medium">
                        {language === 'fr' ? 'Brouillon' : 'Draft'}
                      </span>
                      <span className="font-semibold text-foreground truncate">{draft.template_name}</span>
                    </div>
                    {draft.client_name && <p className="text-sm text-muted-foreground">{draft.client_name}</p>}
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                      <Calendar size={12} />
                      {new Date(draft.updated_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => navigate(`/contract/${draft.template_id}${draft.client_id ? `?client_id=${draft.client_id}` : ''}`)}
                      className="px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-xl hover:bg-primary/90">
                      {language === 'fr' ? 'Reprendre' : 'Resume'}
                    </button>
                    <button onClick={() => deleteDraft.mutate(draft.id)}
                      className="p-2 text-destructive hover:bg-destructive/10 rounded-xl">
                      <Trash2 size={18} />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <p className="text-6xl mb-4">📋</p>
            <p className="text-xl">{t('history.noContracts')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pagination.paged.map((contract, i) => (
              <motion.div
                key={contract.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="card-elevated p-5 flex items-center gap-4"
              >
                {contract.signatureDataUrl && (
                  <img
                    src={contract.signatureDataUrl}
                    alt="Signature"
                    className="w-16 h-12 object-contain rounded-lg bg-secondary p-1"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-lg font-bold truncate">{contract.clientName}</p>
                  <p className="text-sm text-muted-foreground truncate">{contract.templateName}</p>
                  <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Mail size={12} /> {contract.clientEmail}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar size={12} />
                      {new Date(contract.signedAt).toLocaleDateString(
                        language === 'fr' ? 'fr-FR' : language === 'es' ? 'es-ES' : 'en-US'
                      )}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleExportPdf(contract.id)}
                  className="touch-button p-3 bg-primary/10 text-primary rounded-xl"
                  title={language === 'fr' ? 'Exporter en PDF' : 'Export PDF'}
                >
                  <Download size={20} />
                </button>
                <button
                  onClick={() => handlePreviewPdf(contract.id)}
                  className="touch-button p-3 bg-secondary rounded-xl"
                  title={language === 'fr' ? 'Aperçu PDF' : 'Preview PDF'}
                >
                  <Eye size={20} />
                </button>
                <button
                  onClick={() => handleShare(contract.id)}
                  className="touch-button p-3 bg-secondary rounded-xl"
                  title={language === 'fr' ? 'Partager' : language === 'es' ? 'Compartir' : 'Share'}
                >
                  <Share2 size={20} />
                </button>
                <button
                  onClick={() => setDeleteId(contract.id)}
                  className="touch-button p-3 bg-destructive/10 text-destructive rounded-xl"
                  title={language === 'fr' ? 'Supprimer' : 'Delete'}
                >
                  <Trash2 size={20} />
                </button>
              </motion.div>
            ))}
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalItems}
              pageSize={20}
              onPage={pagination.setPage}
            />
          </div>
        )}
      </div>

      {/* In-app PDF preview overlay */}
      <AnimatePresence>
        {previewUrl && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-background flex flex-col"
          >
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-lg font-bold">{t('history.title')}</h2>
              <button onClick={closePreview} className="touch-button p-3 bg-secondary rounded-xl">
                <X size={24} />
              </button>
            </div>
            <iframe
              src={previewUrl}
              className="flex-1 w-full"
              title="PDF Preview"
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete confirmation dialog */}
      <AnimatePresence>
        {deleteId && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            onClick={() => setDeleteId(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-card rounded-2xl p-6 max-w-sm w-full space-y-4 shadow-xl border border-border"
            >
              <h3 className="text-lg font-bold">
                {language === 'fr' ? 'Supprimer ce contrat ?' : language === 'es' ? '¿Eliminar este contrato?' : 'Delete this contract?'}
              </h3>
              <p className="text-muted-foreground text-sm">
                {language === 'fr' ? 'Cette action est irréversible.' : language === 'es' ? 'Esta acción es irreversible.' : 'This action cannot be undone.'}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteId(null)}
                  className="touch-button flex-1 py-3 bg-secondary rounded-xl font-semibold"
                >
                  {language === 'fr' ? 'Annuler' : language === 'es' ? 'Cancelar' : 'Cancel'}
                </button>
                <button
                  onClick={confirmDelete}
                  className="touch-button flex-1 py-3 bg-destructive text-destructive-foreground rounded-xl font-semibold"
                >
                  {language === 'fr' ? 'Supprimer' : language === 'es' ? 'Eliminar' : 'Delete'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default History;
